import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef } from "react";
import { toast } from "sonner";

import type { AgendaItem } from "@elation/api-client/src/stitch-web";
import {
  getListAgendaItemsQueryKey,
  useReorderAgendaItems,
} from "@elation/api-client/src/stitch-web";

import type { CachedAgendaItemList } from "@src/features/agenda/domain";
import { getServerErrorMessage } from "@src/shared/utils/http/errors";
import { captureException } from "@src/shared/utils/observability/sentry";

import { applyServerReorderToCurrentItems } from "./apply-server-reorder";
import { isPendingAgendaItemId } from "./reconcile-ordered-agenda-items";

type ReorderMutationContext = {
  previous: CachedAgendaItemList | undefined;
};

/**
 * Encounter-scoped mutation key for agenda reorder persistence.
 */
export function getAgendaReorderMutationKey(encounterId: number) {
  return ["reorderAgendaItems", encounterId] as const;
}

function setCachedItems(
  queryClient: ReturnType<typeof useQueryClient>,
  encounterId: number,
  items: AgendaItem[],
): void {
  const queryKey = getListAgendaItemsQueryKey(encounterId);
  queryClient.setQueryData<CachedAgendaItemList>(queryKey, (old) => ({
    ...old,
    data: { ...old?.data, items },
  }));
}

/**
 * Persists one agenda reorder at a time per encounter via TanStack mutation
 * lifecycle. DnD should stay disabled while `isLocalReorderActive` is true.
 */
export function useAgendaReorder(encounterId: number | undefined) {
  const queryClient = useQueryClient();
  const isSchedulingRef = useRef(false);
  const pendingProjectionsRef = useRef(new Map<number, AgendaItem[]>());

  const mutationKey =
    encounterId != null
      ? getAgendaReorderMutationKey(encounterId)
      : (["reorderAgendaItems", "idle"] as const);

  const pendingMutationCount = useIsMutating({ mutationKey });
  const isLocalReorderActive = encounterId != null && pendingMutationCount > 0;

  const { mutate } = useReorderAgendaItems({
    mutation: {
      mutationKey,
      retry: false,
      onMutate: async ({ encounterId: id }) => {
        const queryKey = getListAgendaItemsQueryKey(id);
        await queryClient.cancelQueries({ queryKey });
        const previous =
          queryClient.getQueryData<CachedAgendaItemList>(queryKey);
        const items = pendingProjectionsRef.current.get(id) ?? [];
        pendingProjectionsRef.current.delete(id);
        setCachedItems(queryClient, id, items);
        return { previous } satisfies ReorderMutationContext;
      },
      onSuccess: (result, { encounterId: id }) => {
        const current =
          queryClient.getQueryData<CachedAgendaItemList>(
            getListAgendaItemsQueryKey(id),
          )?.data.items ?? [];
        setCachedItems(
          queryClient,
          id,
          applyServerReorderToCurrentItems(current, result.data.items),
        );
      },
      onError: (error, { encounterId: id }, context) => {
        captureException(error, {
          tags: { feature: "agenda-v2", action: "reorder" },
          extra: { encounterId: id },
        });
        toast.error(
          getServerErrorMessage(error) ??
            "Unable to save agenda order. Please try again.",
        );
        const queryKey = getListAgendaItemsQueryKey(id);
        const previous = (context as ReorderMutationContext | undefined)
          ?.previous;
        if (previous !== undefined) {
          queryClient.setQueryData(queryKey, previous);
        } else {
          queryClient.removeQueries({ queryKey });
        }
      },
      onSettled: (_data, _error, { encounterId: id }) => {
        void queryClient.invalidateQueries({
          queryKey: getListAgendaItemsQueryKey(id),
        });
      },
    },
  });

  const scheduleReorder = useCallback(
    (items: AgendaItem[]) => {
      if (encounterId == null || isSchedulingRef.current) return;
      if (
        queryClient.isMutating({
          mutationKey: getAgendaReorderMutationKey(encounterId),
        }) > 0
      ) {
        return;
      }

      const orderedIds = items
        .map((item) => item.id)
        .filter((id) => !isPendingAgendaItemId(id));
      if (orderedIds.length === 0) return;

      isSchedulingRef.current = true;
      pendingProjectionsRef.current.set(encounterId, items);
      mutate({ encounterId, data: { orderedIds } });
      queueMicrotask(() => {
        isSchedulingRef.current = false;
      });
    },
    [encounterId, mutate, queryClient],
  );

  return { scheduleReorder, isLocalReorderActive };
}
